import React, { useMemo } from "react";
import { PICK_TYPES } from "src/common-services";
import Dropdown from "../Dropdown";
import ButtonGroup, { ButtonGroupItem } from "../ButtonGroup";
import styles from "./style.scss";
import Tooltip from "src/components/Tooltip";
import Spacer from "../Spacer";
import { WebSportItem } from "src/constants";

interface GameLinesNavProps {
  setLineType(lineType: string): void;
  setStateIndex(stateIndex: number): void;
  selectedLineType: string;
  selectedStateIndex: number;
  currentSportObj: WebSportItem | null;
  hideCombined?: boolean;
  size: any;
}

const DEFAULT_LINE_TYPES: ButtonGroupItem[] = [
  { name: "Combined", key: "combined" },
  { key: "spread", name: "Spread" },
  { key: "moneyline", name: "Moneyline" },
  { key: "total", name: "Total" },
  { key: "1Hspread", name: "1H Spread" },
  { key: "1Hmoneyline", name: "1H Moneyline" },
  { key: "1Htotal", name: "1H Total" },
  { key: "2Hspread", name: "2H Spread" },
  { key: "2Hmoneyline", name: "2H Moneyline" },
];

const WC_FALLBACK_LINE_TYPES: ButtonGroupItem[] = [
  { key: "moneyline", name: "Moneyline" },
  { key: "total", name: "Total" },
  { key: "spread", name: "Spread" },
  { key: "1Hmoneyline", name: "1H Moneyline" },
  { key: "1Hspread", name: "1H Spread" },
];

const GameLineNav = ({
  currentSportObj,
  selectedLineType,
  setLineType,
  hideCombined = false,
  size,
}: GameLinesNavProps) => {
  const { selectedTypeIndex, lineTypes } = useMemo(() => {
    const types = [];
    currentSportObj?.pickTypes.forEach((pick: string) => {
      types.push({
        key: PICK_TYPES[pick].filterKey,
        name: PICK_TYPES[pick].shortName,
      });
    });

    if (!hideCombined && !["NHL", "MLB", "WC"].includes(currentSportObj?.key ?? "")) {
      types.unshift({
        name: "Combined",
        key: "combined",
      });
    }

    const index = Math.max(
      0,
      types.findIndex((c) => c.key === selectedLineType)
    );

    return { selectedTypeIndex: index, lineTypes: types };
  }, [currentSportObj?.key, selectedLineType]);

  const favLineType = currentSportObj?.favoriteLine ?? "moneyline";
  const fallbackLineTypes = hideCombined ? WC_FALLBACK_LINE_TYPES : DEFAULT_LINE_TYPES;
  const mobileItems = currentSportObj ? lineTypes.slice(1) : fallbackLineTypes.slice(1);
  const mobileSelectedIndex = Math.max(0, selectedTypeIndex - (hideCombined ? 0 : 1));

  return (
    <div className={`nav-header-con ${size}`}>
      <div className="line-text">
        Lines
        <Spacer size={5} />
        <Tooltip title="Lines">
          <p className="popover-text">View the odds for each matchup per sportsbook to find the best opportunities.</p>
          <p className="popover-text">Line are updated within every 5 minutes.</p>
          <p className="popover-text">If they look like have not updated, refresh the page.</p>
        </Tooltip>
      </div>
      {/* On big screens we have all options as a button group */}
      <ButtonGroup
        className={`button-group ${size}`}
        onSelectItem={(item) => setLineType(item.key ?? favLineType)}
        selectedIndex={selectedTypeIndex}
        items={currentSportObj ? lineTypes : fallbackLineTypes}
      />
      {/* On medium screens we have all options as a drop down */}
      <Dropdown
        className={`line-dropdown ${size}`}
        onSelectItem={(item) => setLineType(item.key ?? favLineType)}
        selectedIndex={selectedTypeIndex}
        items={currentSportObj ? lineTypes : fallbackLineTypes}
      />
      {/* On small screens and under we do not have combined as an option and we have a dropdown */}
      <Dropdown
        className={`mobile-line-dropdown ${size}`}
        onSelectItem={(item) => setLineType(item?.key ?? favLineType)}
        selectedIndex={mobileSelectedIndex}
        items={mobileItems}
      />
      <style jsx global>
        {styles}
      </style>
    </div>
  );
};

export default GameLineNav;
